import { UAApps } from '@unifyapps/defs/types/apps';
import { UAResources } from '@unifyapps/defs/types/resources';
import type { BlockType } from '@unifyapps/defs/types/block';
import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import type { DataTableComponentType } from '@unifyapps/defs/blocks/Table/DataTable/types';
import type { EntityContentType } from '@unifyapps/defs/blocks/Comments/types';
import { StandardDataSourceIds } from '@unifyapps/defs/types/dataSourceIds';
import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import { Modes } from '@unifyapps/defs/blocks/Calendar/types';
import { getSelectedDataSourcePath } from '../../../../utils/getSelectedDataSourcePath';
import {
  getEntityTypeFromDataSourceId,
  getIsDataSourceAnEntityType,
} from '../../../utils/entityDataSource';

interface GetDataTableEntityDataSourceParams {
  blockId: string;
  dataSourceId: string;
  activeInterfacePageId: string;
  inputs?: DataTableComponentType['content']['inputs'];
}

export const getDataTableEntityDataSource = (
  params: GetDataTableEntityDataSourceParams,
): DataSourceEntity => {
  const { blockId, dataSourceId, activeInterfacePageId, inputs } = params;

  return {
    id: dataSourceId,
    entityType: UAEntityTypes.DataSource,
    properties: {
      type: 'APPLICATION',
      interfacePageId: activeInterfacePageId,
      name: `Entity Data Source: ${dataSourceId}`,
      context: {
        appName: UAApps.StorageByUA,
        resourceName: UAResources.StorageByUAFetchRecords,
      },
      inputs: {
        numberOfRecordsToFetch: 'MULTIPLE',
        includeTotalCount: true,
        sortBy: inputs?.sorts,
        triggerInputCondition: inputs?.filters,
        searchField: inputs?.searchField,
        object_type: getEntityTypeFromDataSourceId(dataSourceId),
        page: {
          offset: `{{ ${blockId}['content']['page']['offset'] }}`,
          limit: `{{ ${blockId}['content']['page']['size'] }}`,
        },
      },
    },
  };
};

export const getEntityTypeDataSourceFromBlock = (
  block: BlockType,
  activeInterfacePageId: string,
): DataSourceEntity | undefined => {
  switch (block.component.componentType) {
    case 'Table':
      {
        const { data, inputs } = block.component.content;
        const { dataSourceId } = getSelectedDataSourcePath({ data });

        if (dataSourceId && getIsDataSourceAnEntityType(dataSourceId)) {
          return getDataTableEntityDataSource({
            blockId: block.id,
            dataSourceId,
            activeInterfacePageId,
            inputs,
          });
        }
      }
      break;
    case 'Comments':
      {
        const { data, assetId } = block.component.content as EntityContentType;
        const { dataSourceId } = getSelectedDataSourcePath({ data });

        if (dataSourceId && getIsDataSourceAnEntityType(dataSourceId)) {
          return {
            id: dataSourceId,
            entityType: UAEntityTypes.DataSource,
            properties: {
              standardDataSourceId: StandardDataSourceIds.FetchEntityComments,
              type: 'APPLICATION',
              interfacePageId: activeInterfacePageId,
              name: `Entity Data Source: ${dataSourceId}`,
              context: {
                appName: UAApps.CommentsByUnifyApps,
                resourceName: UAResources.CommentsByUAFindComments,
              },
              inputs: {
                assetId,
                assetClass: getEntityTypeFromDataSourceId(dataSourceId),
              },
            },
          };
        }
      }
      break;
    case 'EventsCalendar': {
      if (block.component.content.mode === Modes.Entity && block.component.content.events?.data) {
        const { dataSourceId } = getSelectedDataSourcePath({
          data: block.component.content.events.data,
        });

        if (dataSourceId && getIsDataSourceAnEntityType(dataSourceId)) {
          return {
            id: dataSourceId,
            entityType: UAEntityTypes.DataSource,
            properties: {
              standardDataSourceId: StandardDataSourceIds.FetchCalendarEvents,
              type: 'APPLICATION',
              interfacePageId: activeInterfacePageId,
              name: `Entity Data Source: ${dataSourceId}`,
              context: {
                appName: UAApps.CalendarByUA,
                resourceName: UAResources.CalendarByUAFetchEvents,
              },
              inputs: {
                triggerInputCondition: {
                  filters: [
                    {
                      filters: [
                        {
                          filter: {
                            value: `{{ Number(${block.id}['currentView']['start']) }}`,
                            operator: 'GTE',
                          },
                          property: 'properties_start_date',
                        },
                        {
                          filter: {
                            value: `{{ Number(${block.id}['currentView']['end']) }}`,
                            operator: 'LTE',
                          },
                          property: 'properties_end_date',
                        },
                      ],
                      operator: 'AND',
                    },
                    {
                      filters: [
                        {
                          filter: {
                            value: `{{ Number(${block.id}['currentView']['start']) }}`,
                            operator: 'GTE',
                          },
                          property: 'properties_start_dateTime',
                        },
                        {
                          filter: {
                            value: `{{ Number(${block.id}['currentView']['end']) }}`,
                            operator: 'LTE',
                          },
                          property: 'properties_end_dateTime',
                        },
                      ],
                      operator: 'AND',
                    },
                  ],
                  operator: 'OR',
                },
              },
            },
          };
        }
      }
      break;
    }
    default:
  }
};
